import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

/** Every output-affecting screener parameter, derived from the wire contract. */
export type ScreenerQueryParams = Query<'screenerScreen'>;

/**
 * Screener domain (fox F12) — the scored-universe search, its facet lists, and
 * the user's saved filter presets.
 *
 * The key carries the WHOLE parameter object, because every field of it changes
 * the answer: a cached page for `minPoints=70` must not be served to a request
 * for `minPoints=80`, and the sort is part of the page, not a view over it.
 */
export function createScreenerQueries(client: StoxlyClient) {
  return createQueryKeys('screener', {
    // ['screener', 'search', { …params }]
    search: (params: ScreenerQueryParams) => ({
      queryKey: [params],
      queryFn: ({ signal }) => client.screenerScreen({ queries: params, signal }),
    }),
    // ['screener', 'facets'] — the universe's industries/countries/cap buckets.
    facets: {
      queryKey: null,
      queryFn: ({ signal }) => client.screenerFacets({ signal }),
    },
    // ['screener', 'presets'] — the reader's own saved filter sets.
    presets: {
      queryKey: null,
      queryFn: ({ signal }) => client.screenerPresetsList({ signal }),
    },
  });
}

export function createScreenerMutations(client: StoxlyClient) {
  return {
    createPreset: {
      mutationFn: (body: Body<'screenerPresetsCreate'>) => client.screenerPresetsCreate(body),
    },
    deletePreset: {
      mutationFn: (id: string) => client.screenerPresetsDelete(undefined, { params: { id } }),
    },
  };
}
